/**
 * The Plans tab's URL view state and sort columns (DNKADM-0430 P1-F).
 *
 * URL params on `/membership-credits/plans` (all optional; a default is never written):
 * - `q`: search on the plan name.
 * - `status`: the Active/Inactive/All filter (default `active`).
 * - `sort` + `dir`: the column sort. The server sends plans in no defined order, so the
 *   default is Plan name ascending.
 *
 * **Row content (DNKADM-0496).** Name, status (Draft / Turned off / Active), price and billing
 * interval, and member count. The Type, Who can buy and Term columns are gone, so their sort
 * keys are too: an old bookmarked `sort=type|audience|term` falls back to the default sort.
 */
import type { MembershipPlan } from '@/api/membershipPlans';
import { formatMoney } from '@/utils/v2Format';
import { matchesSearch } from '@/components/list/listSearch';
import type { SortState, SortValue } from '@/components/list/listSort';

export const PLANS_URL_DEFAULTS = { q: '', status: 'active', sort: '', dir: '' };

export type PlanSortKey = 'name' | 'price' | 'members' | 'status';
export const PLAN_SORT_KEYS: readonly PlanSortKey[] = ['name', 'price', 'members', 'status'];
export const PLAN_DEFAULT_SORT: SortState<PlanSortKey> = { key: 'name', dir: 'asc' };

/** Display name for a plan (its name/tier, falling back to the id). */
export function planName(plan: MembershipPlan): string {
  return plan.name.length > 0 ? plan.name : plan.id;
}

/** "Every 30 days": the plan's billing interval (its term). */
export function planTermLabel(plan: MembershipPlan): string {
  return `Every ${plan.termDays} ${plan.termDays === 1 ? 'day' : 'days'}`;
}

/** The plan price in the tenant currency (a plain two-decimal number until it resolves). */
export function planPriceLabel(plan: MembershipPlan, currency: string | null): string {
  return currency ? formatMoney(plan.price, currency) : plan.price.toFixed(2);
}

/** The list's price cell: "$99.00 every 30 days". */
export function planPriceAndTermLabel(plan: MembershipPlan, currency: string | null): string {
  return `${planPriceLabel(plan, currency)} ${planTermLabel(plan).toLowerCase()}`;
}

/** The Who-can-buy label. */
export function planAudienceLabel(plan: MembershipPlan): string {
  return plan.staffOnly ? 'Staff only' : 'Members and staff';
}

/** The lifecycle status words (the service page's, DNKADM-0493): what the Status column sorts by. */
export function planStatusLabel(plan: MembershipPlan): string {
  if (plan.status === 'DRAFT') return 'Draft';
  if (plan.status === 'INACTIVE') return 'Turned off';
  return 'Active';
}

/**
 * What a plan row shows (and sorts by) in each column. A plan with no `impact` shows a
 * dash for Members, so it sorts with the blanks (last, both directions).
 */
export function planSortValue(plan: MembershipPlan, key: PlanSortKey): SortValue {
  switch (key) {
    case 'name':
      return planName(plan);
    case 'price':
      return plan.price;
    case 'members':
      return plan.impact?.activeMemberships ?? null;
    case 'status':
      return planStatusLabel(plan);
  }
}

/** The Plans search: the plan name. */
export function planMatches(plan: MembershipPlan, query: string): boolean {
  return matchesSearch(query, [planName(plan)]);
}
